import { useSelect } from 'core/components/common/Select/useSelect';
import { useIncidentDetailForHiroshima } from './useIncidentDetailForHiroshima';
import { useParams } from 'react-router-dom';
import { useNavigateWithParams } from 'core/hooks/useNavigateWithParams';
import { IncidentInfoWrapper } from 'core/components/JianDetail';
import { IncidentInformation } from 'core/components/JianDetail/IncidentInformation';
import { formatDateTime } from 'core/utils/date';
import { useTab } from 'core/components/common/Tab/useTab';
import { EmergencyTeam } from 'core/components/JianDetail/EmergencyTeam';
import { Select } from 'core/components/common/Select';
import { Tab } from 'core/components/common/Tab';
import { PatientDetailSection } from './PatientDetailSection';

// 広島事案詳細
export const IncidentDetail = () => {
  const {
    incidentDetailData: { incidentInfo, emsTeamInfo, patientInfo: patients },
  } = useIncidentDetailForHiroshima();

  const { patientId } = useParams();
  const selectedPatient = patients?.find(p => p.id === patientId);

  if (!selectedPatient)
    throw new Error(`Selected patient not found. patientId: ${patientId}`);

  const navigateWithParams = useNavigateWithParams();

  const { selected, options, handleChange } = useSelect({
    options: patients.map(patient => ({
      value: patient.id,
      label: patient.baseInfo.name,
    })),
    defaultValue: selectedPatient.id,
    onChange: (event: React.ChangeEvent<HTMLSelectElement>) => {
      const selectedPatientId = event.target.value;
      navigateWithParams(
        `/jian-detail/${incidentInfo.id}/${selectedPatientId}`
      );
    },
  });

  const patientTimeSeries = selectedPatient.timeSeries;
  const emergencyResponseTimeline = [
    {
      title: '覚知',
      content: incidentInfo.recognitionAt
        ? formatDateTime(incidentInfo.recognitionAt, 'MM/dd HH:mm')
        : '',
    },
    {
      title: '指令',
      content: incidentInfo.telegramAt
        ? formatDateTime(incidentInfo.telegramAt, 'MM/dd HH:mm')
        : '',
    },
    {
      title: '出動',
      content: incidentInfo.departureAt
        ? formatDateTime(incidentInfo.departureAt, 'MM/dd HH:mm')
        : '',
    },
    {
      title: '現着',
      content: incidentInfo.siteArrivalAt
        ? formatDateTime(incidentInfo.siteArrivalAt, 'MM/dd HH:mm')
        : '',
    },
    {
      title: '傷病者接触',
      content: patientTimeSeries?.patientContactAt
        ? formatDateTime(patientTimeSeries.patientContactAt, 'MM/dd HH:mm')
        : '',
    },
    {
      title: '車内収容',
      content: patientTimeSeries?.ambulanceAccommodationAt
        ? formatDateTime(
            patientTimeSeries.ambulanceAccommodationAt,
            'MM/dd HH:mm'
          )
        : '',
    },
    {
      title: '現発',
      content: patientTimeSeries?.siteDepartureAt
        ? formatDateTime(patientTimeSeries.siteDepartureAt, 'MM/dd HH:mm')
        : '',
    },
    {
      title: '病着',
      content: patientTimeSeries?.hospitalArrivalAt
        ? formatDateTime(patientTimeSeries.hospitalArrivalAt, 'MM/dd HH:mm')
        : '',
    },
    {
      title: '引揚',
      content: incidentInfo.hospitalDepartureAt
        ? formatDateTime(incidentInfo.hospitalDepartureAt, 'MM/dd HH:mm')
        : '',
    },
    {
      title: '帰着',
      content: incidentInfo.returnAt
        ? formatDateTime(incidentInfo.returnAt, 'MM/dd HH:mm')
        : '',
    },
  ];

  const { selectedTabValue, handleClickTab } = useTab({
    tabs: [...HIROSHIMA_PATIENT_DETAIL_TABS],
    defaultTabValue: 'baseInfo',
  });

  return (
    <>
      <IncidentInfoWrapper>
        <IncidentInformation
          incidentNo={incidentInfo.incidentNo}
          incidentType={incidentInfo.incidentType}
          telegramTargetAddress={incidentInfo.telegramTargetAddress}
          targetAddressOther={incidentInfo.telegramTargetAddress}
          timeSeries={emergencyResponseTimeline}
        />
        <EmergencyTeam emsTeam={emsTeamInfo} />
      </IncidentInfoWrapper>
      <div className="bg-surface pt-[60px]">
        <div className="max-w-[1280px] px-[40px] m-auto flex flex-col gap-[24px]">
          <div className="ml-auto">
            <Select
              options={options}
              onChange={handleChange}
              selected={selected?.value ?? ''}
            />
          </div>
          <Tab
            tabs={[...HIROSHIMA_PATIENT_DETAIL_TABS]}
            selectedTabValue={selectedTabValue}
            onClick={handleClickTab}
          />
        </div>
      </div>
      {selected && (
        <PatientDetailSection
          patient={selectedPatient}
          selectedTab={selectedTabValue}
        />
      )}
    </>
  );
};

const HIROSHIMA_PATIENT_DETAIL_TABS = [
  {
    value: 'baseInfo',
    label: '基本情報',
  },

  {
    value: 'requestSummary',
    label: '要請概要欄',
  },
  {
    value: 'findingsEmsContact',
    label: '身体所見',
  },
  {
    value: 'vitals',
    label: 'バイタル',
  },
  {
    value: 'photo',
    label: '写真',
  },
  {
    value: 'specialTreatment',
    label: '特定行為等',
  },
  {
    value: 'incidentSituation',
    label: '状況評価',
  },
  {
    value: 'cpaMode',
    label: 'CPAモード',
  },
  {
    value: 'note',
    label: '備考',
  },
  {
    value: 'acceptRequestHistory',
    label: '要請履歴',
  },
  {
    value: 'documents',
    label: 'PDF出力',
  },
] as const;

export type HiroshimaPatientDetailTabKey =
  (typeof HIROSHIMA_PATIENT_DETAIL_TABS)[number]['value'];
